import React, { FC, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import isEmpty from 'lodash/isEmpty'
import ControlledPhoneInput from 'transfix-form/components/ControlledPhoneInput'
import { Button } from 'transfix-ui/components/Button'
import { Box } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import * as yup from 'yup'
import { FullPageOverlay } from 'apps/fleet-planner/components/modals/full-page-overlay/FullPageOverlay'
import { useUserContext } from 'apps/fleet-planner/context'
import { ToastState } from 'transfix-ui'
import { getInlineMessageProps } from '../modals/CreateLoadOverlay'

export const SubscriptionModalSchema = yup
  .object({
    cellPhone: yup
      .string()
      .required('Please enter your cell phone number.')
      .min(10, 'Cell phone number is not valid'),
  })
  .required()

interface ISubscriptionModalProps {
  isLoading: boolean
  isOpen: boolean
  onClose: () => void
  onSubmit: () => void
  openToast: (newToastState: Omit<ToastState, 'toastOpen'>) => void
}

interface IFormData {
  cellPhone: string
}

export const SubscriptionModal: FC<ISubscriptionModalProps> = ({
  isLoading,
  isOpen,
  onClose,
  onSubmit,
  openToast,
}) => {
  const { getUser, updateUserInfo } = useUserContext()
  const { cellPhone } = getUser() || {}
  const { control, errors: formErrors, handleSubmit, reset } = useForm({
    mode: 'all',
    resolver: yupResolver(SubscriptionModalSchema),
    defaultValues: SubscriptionModalSchema.cast({
      cellPhone: cellPhone || '',
    }),
  })

  useEffect(() => {
    if (cellPhone) {
      reset({ cellPhone })
    }
  }, [cellPhone, reset])

  const submitUpdateCellPhone = async (formData: IFormData) => {
    try {
      await updateUserInfo({ user: { cellPhone: formData?.cellPhone } })

      openToast({
        toastType: 'default',
        toastMessage: 'Mobile number updated.',
      })
      onSubmit()
    } catch (e) {
      openToast({
        toastType: 'alert',
        toastMessage: 'Something went wrong. Please contact us.',
      })
    }
  }

  const submitFormHandler = handleSubmit(submitUpdateCellPhone)
  const { isMobile } = useBreakpointContext()
  const inlineMessageProps = getInlineMessageProps(
    !isEmpty(formErrors) ? 'Please fill out the required fields below' : ''
  )

  return (
    <FullPageOverlay
      title={cellPhone ? 'Update Mobile Number' : 'Add Mobile Number'}
      isOpen={isOpen}
      onClose={onClose}
      fullScreen={isMobile}
      inlineMessageProps={inlineMessageProps}
      maxWidth='sm'
      onSubmit={submitFormHandler}
      footer={
        <Button
          color='primary'
          disabled={isLoading}
          fullWidth={isMobile}
          size='large'
          onClick={submitFormHandler}>
          {cellPhone ? 'Update' : 'Save'}
        </Button>
      }>
      <Box p={1}>
        <ControlledPhoneInput
          control={control}
          name='cellPhone'
          PhoneInputProps={{
            placeholder: '(000) 000-0000',
            large: true,
            label: 'Mobile Number',
            invalid: !!formErrors.cellPhone,
            validationMessage: formErrors?.cellPhone?.message,
            className: 'fs-mask',
          }}
        />
      </Box>
    </FullPageOverlay>
  )
}
